import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import type { RootState } from '../../../../../store/redux/store/store';
import { getCompanyRules, updateCompanyRules } from '../../../../../api/General/Company';
import { updateCompanyRules as updateCompanyRulesAction } from '../../../../../store/redux/actions/userActionCreator';
import { GetCompanyGrossPays } from '../../../../../api/Payroll/grossPay';
import type { CompanyGrossPayView } from '../../../../../models/payroll';
import { PayrollSalaryBaseMode, PAYROLL_BASIS_DEFAULTS } from '../../../../../models/general';
import type { CompanyRulesInput } from '../../../../../models/general';
import { Spinner } from '../../../../../components/ui/Spinner';
import StickyActionBar from '../../../../../components/ui/StickyActionBar';
import AppIcon from '../../../../../components/AppIcon';
import { cn } from '../../../../../utils/cn';
import { isBasicSalaryComponent } from '../../../../../models/payroll';

/**
 * HR-facing editor for the STATUTORY salary base: which salary base the end-of-service gratuity —
 * and with it the GOSI / GPSSA / UAE-pension wage base — is computed from.
 *
 * 🔴 CO-304 — the OVERTIME half of this panel MOVED to Settings/Payroll/AttendancePaySettings
 * (`/settings/payroll/attendance`). `payrollUseAttendanceOvertime`, `payrollRequireOvertimeApproval`,
 * `payrollOvertimeBaseMode`, both multipliers and `payrollHoursPerMonth` are edited there and must
 * NOT be edited here as well: two screens writing one flag is worse than one screen in the wrong
 * place. The move happened because this page's route is registered for UAE/KSA tenants only
 * (`App.tsx`, guarded on `complianceCountry`, and `useFilteredNavigation.ts` filters the nav entry
 * the same way), so most tenants could not reach any of it. End of service is genuinely statutory
 * and stays.
 *
 * 🔴 THE POST SENDS THE WHOLE CompanyRulesView BACK, NOT JUST THIS ONE FIELD. CompanyRulesInput has
 * non-nullable members (attendanceImageConfig, breakConfig, timesheetCreationDay, the
 * scheduledShift* thresholds ...) that CompanyService.UpdateCompanyRules assigns UNCONDITIONALLY,
 * so a one-field POST would deserialize those to their C# defaults and wipe the tenant's attendance
 * configuration. Same round-trip pattern as EngagementJobsTab and
 * Settings/Attendance/workscheduleconfig.tsx. Only `payrollEosBaseMode` is ever mutated locally;
 * everything else — the overtime fields included — rides along untouched from the GET.
 *
 * The field is nullable on the wire and null means "server default", so the form reads through
 * PAYROLL_BASIS_DEFAULTS with `??` only. The base mode is a STRING enum ('Auto' / 'BasicOnly' /
 * 'Gross') because Program.cs registers a global Newtonsoft StringEnumConverter, so 'Auto' is a
 * real configured value and must never be treated as unset by a truthiness test.
 */

interface BasisForm {
  eosBaseMode: PayrollSalaryBaseMode;
}

const BASE_MODES: PayrollSalaryBaseMode[] = [
  PayrollSalaryBaseMode.Auto,
  PayrollSalaryBaseMode.BasicOnly,
  PayrollSalaryBaseMode.Gross,
];

const PayrollCalculationBasisPanel: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const companyId = useSelector((state: RootState) => state.user.company?.id);

  const [rules, setRules] = useState<CompanyRulesInput | null>(null);
  const [form, setForm] = useState<BasisForm | null>(null);
  const [baseline, setBaseline] = useState<BasisForm | null>(null);
  const [basicComponents, setBasicComponents] = useState<CompanyGrossPayView[]>([]);
  const [componentsKnown, setComponentsKnown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setSaved(false);
    try {
      const [rulesResult, grossPays] = await Promise.all([
        getCompanyRules(companyId),
        GetCompanyGrossPays().catch(() => null),
      ]);

      if (rulesResult.errors?.length || !rulesResult.payload) {
        setError(
          rulesResult.errors?.[0]?.description ||
            t('settings.payrollBasis.loadFailed', 'Could not load the payroll calculation basis.')
        );
        return;
      }

      const payload = rulesResult.payload;
      const loaded: BasisForm = {
        eosBaseMode: payload.payrollEosBaseMode ?? PAYROLL_BASIS_DEFAULTS.eosBaseMode,
      };

      setRules(payload as unknown as CompanyRulesInput);
      setForm(loaded);
      setBaseline(loaded);

      if (grossPays && grossPays.payload) {
        setBasicComponents(grossPays.payload.filter(isBasicSalaryComponent));
        setComponentsKnown(true);
      }
    } catch (err) {
      console.error('Failed to load payroll calculation basis:', err);
      setError(t('settings.payrollBasis.loadFailed', 'Could not load the payroll calculation basis.'));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = useCallback((changes: Partial<BasisForm>) => {
    setForm((prev) => (prev ? { ...prev, ...changes } : prev));
    setSaved(false);
  }, []);

  const dirty = useMemo(() => {
    if (!form || !baseline) return false;
    return (Object.keys(baseline) as (keyof BasisForm)[]).some((k) => baseline[k] !== form[k]);
  }, [form, baseline]);

  const handleSave = async () => {
    if (!companyId || !rules || !form) return;
    setSaving(true);
    setError(null);
    try {
      const input: CompanyRulesInput = {
        ...rules,
        payrollEosBaseMode: form.eosBaseMode,
      };
      const result = await updateCompanyRules(companyId, input);
      if (result.errors?.length) {
        setError(
          result.errors[0]?.description ||
            t('settings.payrollBasis.saveFailed', 'Could not save the payroll calculation basis.')
        );
        return;
      }
      // Keep the Redux company-rules copy in sync. id/companyId are stripped so they cannot
      // overwrite company.id.
      const rulesOnly: Partial<CompanyRulesInput> = { ...input };
      delete rulesOnly.id;
      delete rulesOnly.companyId;
      dispatch(updateCompanyRulesAction(rulesOnly));
      setRules(input);
      setBaseline(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save payroll calculation basis:', err);
      setError(t('settings.payrollBasis.saveFailed', 'Could not save the payroll calculation basis.'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (baseline) setForm(baseline);
    setSaved(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center rounded-xl border border-border bg-card py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!companyId || !form) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
        {error ||
          t(
            'settings.payrollBasis.noCompany',
            'No company is selected, so the payroll calculation basis cannot be configured.'
          )}
      </div>
    );
  }

  const modeLabel = (mode: PayrollSalaryBaseMode) =>
    ({
      [PayrollSalaryBaseMode.Auto]: t('settings.payrollBasis.modeAuto', 'Automatic'),
      [PayrollSalaryBaseMode.BasicOnly]: t('settings.payrollBasis.modeBasicOnly', 'Basic salary only'),
      [PayrollSalaryBaseMode.Gross]: t('settings.payrollBasis.modeGross', 'Total gross salary'),
    })[mode];

  const modeBody = (mode: PayrollSalaryBaseMode) =>
    ({
      [PayrollSalaryBaseMode.Auto]: t(
        'settings.payrollBasis.modeAutoBody',
        'Uses the component flagged as the basic salary. If nobody has flagged one, it falls back to the only salary component, then to total gross. Recommended, because it never pays zero.'
      ),
      [PayrollSalaryBaseMode.BasicOnly]: t(
        'settings.payrollBasis.modeBasicOnlyBody',
        'Uses only the component explicitly flagged as the basic salary, with no fallback. Strictest reading of the labour law.'
      ),
      [PayrollSalaryBaseMode.Gross]: t(
        'settings.payrollBasis.modeGrossBody',
        'Uses the full gross salary, allowances included. Pays more than the basic-only calculation.'
      ),
    })[mode];

  const noBasicConfigured = componentsKnown && basicComponents.length === 0;

  /** Vertical mode chooser. Rows, not a card grid: each carries a different amount of copy. */
  const modeChooser = (
    value: PayrollSalaryBaseMode,
    onChange: (m: PayrollSalaryBaseMode) => void,
    groupLabel: string,
    disabled: boolean
  ) => (
    <div role="radiogroup" aria-label={groupLabel} className="space-y-2">
      {BASE_MODES.map((mode) => {
        const selected = value === mode;
        return (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(mode)}
            className={cn(
              'w-full rounded-lg border p-3 text-start transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
              selected
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/50 hover:bg-muted/40',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  'grid h-4 w-4 shrink-0 place-items-center rounded-full border',
                  selected ? 'border-primary' : 'border-input'
                )}
                aria-hidden="true"
              >
                {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
              </span>
              <span
                className={cn('text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}
              >
                {modeLabel(mode)}
              </span>
              {mode === PayrollSalaryBaseMode.Auto && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
                  {t('settings.payrollBasis.recommendedTag', 'Recommended')}
                </span>
              )}
            </span>
            <span className="mt-1.5 block max-w-[68ch] ps-6 text-xs text-muted-foreground">
              {modeBody(mode)}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <section aria-labelledby="payroll-basis-heading" className="space-y-4">
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <header className="border-b border-border bg-muted/40 px-5 py-4">
          <h3 id="payroll-basis-heading" className="text-base font-semibold text-foreground">
            {t('settings.payrollBasis.titleEos', 'End of service calculation basis')}
          </h3>
          <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">
            {t(
              'settings.payrollBasis.introEos',
              'This decides what every final settlement pays. It applies whenever a settlement is calculated.'
            )}
          </p>
        </header>

        <div className="px-5 py-5">
          <p className="text-sm font-medium text-foreground">
            {t('settings.payrollBasis.eosBaseLabel', 'Compute gratuity from')}
          </p>
          <div className="mt-2">
            {modeChooser(
              form.eosBaseMode,
              (m) => patch({ eosBaseMode: m }),
              t('settings.payrollBasis.eosBaseLabel', 'Compute gratuity from'),
              saving
            )}
          </div>

          {form.eosBaseMode === PayrollSalaryBaseMode.BasicOnly && noBasicConfigured && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
              <p className="max-w-[70ch]">
                {t(
                  'settings.payrollBasis.eosStrictZero',
                  'No component is flagged as the basic salary, so this mode settles every leaver at zero. Flag a component, or switch to Automatic.'
                )}
              </p>
            </div>
          )}
        </div>

        {/* Where the overtime half went. UAE/KSA HR users have muscle memory for this screen, so the
            pointer has to name what moved rather than just linking. */}
        <div className="border-t border-border bg-muted/20 px-5 py-4">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <AppIcon name="Info" size={14} className="shrink-0" />
            <span className="max-w-[70ch]">
              {t(
                'settings.payrollBasis.movedNotice',
                'Overtime pay, its approval requirement, the overtime multipliers and the hourly-rate divisor are now on their own page, available to every company.'
              )}
            </span>
            <Link
              to="/settings/payroll/attendance"
              className="font-medium text-primary underline underline-offset-2"
            >
              {t('settings.payrollBasis.movedLink', 'Open overtime and attendance pay')}
            </Link>
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <AppIcon name="AlertCircle" size={16} className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {saved && (
        <p className="flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
          <AppIcon name="Check" size={15} className="shrink-0" />
          {t('settings.payrollBasis.saved', 'Payroll calculation basis saved.')}
        </p>
      )}

      <StickyActionBar
        hasUnsavedChanges={dirty}
        isSaving={saving}
        onReset={handleReset}
        onSave={handleSave}
        t={t as (key: string) => string}
        saveLabel={t('settings.payrollBasis.save', 'Save calculation basis')}
      />
    </section>
  );
};

export default PayrollCalculationBasisPanel;
